<script setup lang="ts">
/*
  `/`: the dataspace list when signed in, the landing hero when not.

  THE LIST IS A TABLE, grouped by project. It was a grid of cards, and every card
  said the same five things in a different place — a dataspace is a row of
  attributes, and a table lets the eye run down one of them (which version, which
  role) instead of hunting for it card by card. Grouping by project keeps the
  "Project • Dataspace" pairing the cards spelled out on every title, once per band.

  A row opens the dataspace. Its menu holds what a row is not: deleting it.

  WHERE EACH LIVES — its department, or personal — is a column and a filter, both only once a
  dataspace in the list is in a department (`anyDepartment`): on a server nobody uses departments
  on they would say "Personal" down every row. The list follows the user's own room (a space moved
  in or out of a department, a role changed there): see `useDataspaceListSync`.
*/
import { ref, computed, onMounted, watch } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import {
	mdiDatabaseOutline,
	mdiDatabasePlusOutline,
	mdiFilterCheckOutline,
	mdiFolderOutline,
	mdiMagnify,
	mdiMagnifyClose,
	mdiPencilRuler,
	mdiPlus,
	mdiTrashCanOutline,
} from "@mdi/js";
import {
	HabBadge,
	HabBanner,
	HabButton,
	HabConfirm,
	HabDataCell,
	HabDataTable,
	HabEmptyState,
	HabIcon,
	HabIconTile,
	HabInput,
	HabMenu,
	HabPanel,
	HabRowAction,
	HabSelect,
	HabSkeleton,
	HabToastHost,
} from "@livereader/habitus-vue";
import type { HabDataColumn, HabDataGroup, HabMenuItem } from "@livereader/habitus-vue";
import { sectionTitle } from "@/router/branding";
import { listDataspaces, deleteDataspace, listProjects, type DataspaceResponse } from "@/api";
import BrandMark from "@/shared/components/BrandMark.vue";
import NoLicenseState from "@/shared/components/NoLicenseState.vue";
import PageHeader from "@/shared/components/PageHeader.vue";
import PendingInvitationsBanner from "@/shared/components/PendingInvitationsBanner.vue";
import SpaceLocationBadge from "@/shared/components/SpaceLocationBadge.vue";
import SpaceRestrictionBadge from "@/shared/components/SpaceRestrictionBadge.vue";
import { ensureSession, useAuth } from "@/shared/composables/useAuth";
import { useLicenseGate } from "@/shared/composables/useLicenseGate";
import { useWindowTitle } from "@/shared/composables/useWindowTitle";
import { SEARCH_DEBOUNCE_MS, usePersistedView } from "@/shared/composables/usePersistedView";
import { errorMessage } from "@/shared/utils/errors";
import { VIEW_STATE } from "@/shared/utils/storage";
import {
	ALL_LOCATIONS,
	locationFilterOptions,
	matchesLocationFilter,
} from "@/shared/utils/spaceLocation";
import CreateDataspaceDialog from "@operator/components/dataspace/CreateDataspaceDialog.vue";
import { useOperatorToasts } from "@operator/composables/_shared/useOperatorToasts";
import { useDataspaceListSync } from "@operator/composables/dataspace/useDataspaceListSync";
import { useOperatorAliasStore } from "@operator/stores/_shared/operatorAliasStore";
import { vPageSearch } from "@/shared/utils/pageSearch";

const router = useRouter();
const { t, te } = useI18n();
const { isAuthenticated, login } = useAuth();
// `/` is the landing's route, but signed in it is the Operator's dataspace list, and the tab says so.
useWindowTitle(() => (isAuthenticated.value ? [sectionTitle("operator")] : null));
const toasts = useOperatorToasts();
const aliases = useOperatorAliasStore();

/*
  This page is the Operator's entry, though the landing layout frames it: while the server
  refuses the Operator for want of a license, it says so instead of listing nothing. Opening
  the page asks again.
*/
const licenseGate = useLicenseGate();
licenseGate.clear();

const dataspaces = ref<DataspaceResponse[]>([]);
const loading = ref(true);
const error = ref("");
const {
	state: search,
	reset: resetSearch,
	isDefault: searchIsDefault,
} = usePersistedView(VIEW_STATE.dataspaceSearch, {
	debounceMs: SEARCH_DEBOUNCE_MS,
});
const showCreate = ref(false);
const hasDesignerAccess = ref(false);

const locationFilter = ref(ALL_LOCATIONS);
const anyDepartment = computed(() => dataspaces.value.some((d) => d.department));
const locationOptions = computed(() =>
	locationFilterOptions(dataspaces.value, {
		all: t("spaces.filter.all"),
		personal: t("spaces.filter.personal"),
	}),
);
// A department that is no longer in the list (its last dataspace moved away) cannot stay chosen.
watch(locationOptions, (options) => {
	if (!options.some((o) => o.value === locationFilter.value))
		locationFilter.value = ALL_LOCATIONS;
});
const narrowed = computed(() => locationFilter.value !== ALL_LOCATIONS);

function clearFilters() {
	resetSearch();
	locationFilter.value = ALL_LOCATIONS;
}

const filteredDataspaces = computed(() => {
	const q = search.value.toLowerCase().trim();
	const inLocation = dataspaces.value.filter((d) =>
		matchesLocationFilter(d.department, locationFilter.value),
	);
	if (!q) return inLocation;
	return inLocation.filter(
		(d) =>
			d.label.toLowerCase().includes(q) ||
			d.project_name.toLowerCase().includes(q) ||
			d.description?.toLowerCase().includes(q) ||
			d.project_version.toLowerCase().includes(q),
	);
});

function roleLabel(role: string): string {
	const key = `roles.names.${role}`;
	return te(key) ? t(key) : role;
}

function formatDate(iso: string): string {
	return new Date(iso).toLocaleDateString(undefined, {
		year: "numeric",
		month: "short",
		day: "numeric",
	});
}

const columns = computed<HabDataColumn[]>(() => [
	{ key: "dataspace", label: t("operator.dataspace.column.dataspace"), primary: true },
	...(anyDepartment.value
		? [{ key: "department", label: t("spaces.filter.label"), width: 200 }]
		: []),
	{ key: "version", label: t("operator.dataspace.version"), width: 160 },
	{ key: "role", label: t("invitations.fields.role"), width: 140 },
	{ key: "updated", label: t("operator.dataspace.column.updated"), width: 140, tnum: true },
	{ key: "actions", label: "", width: 56, align: "right" },
]);

/** One band per project, in the order the projects first appear. */
const groups = computed<HabDataGroup[]>(() => {
	const bands = new Map<string, HabDataGroup>();
	for (const d of filteredDataspaces.value) {
		let band = bands.get(d.project_id);
		if (!band) {
			band = { label: d.project_name, icon: mdiFolderOutline, rows: [] };
			bands.set(d.project_id, band);
		}
		band.rows!.push({ id: d.id, interactive: true, updated: formatDate(d.updated_at) });
	}
	return [...bands.values()];
});

const byId = computed(() => new Map(dataspaces.value.map((d) => [d.id, d])));
const dataspaceOf = (id: unknown) => byId.value.get(String(id))!;

function rowMenu(d: DataspaceResponse): HabMenuItem[] {
	if (!d.effective_entitlements.includes("dataspace:delete")) return [];
	return [
		{
			label: t("operator.dataspace.delete"),
			icon: mdiTrashCanOutline,
			tone: "error",
			onSelect: () => (pendingDelete.value = d),
		},
	];
}

// By its alias where it has one, so the URL needs no redirect to say it.
function openDataspace(id: string) {
	router.push(`/operator/${aliases.dataspaceSegment(id)}`);
}

/*
  A link rendered as a HabButton `as="a"`: a real href, so a modified click opens a
  tab the browser's own way, and a plain one routes instead of reloading.
*/
function follow(event: MouseEvent, path: string) {
	if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
	event.preventDefault();
	router.push(path);
}

function onDataspaceCreated(dataspace: DataspaceResponse) {
	dataspaces.value.unshift(dataspace);
	aliases.rememberDataspace(dataspace.id, dataspace.alias);
	toasts.success(t("operator.dataspace.created", { name: dataspace.label }));
}

// ── Delete ────────────────────────────────────────────────────────────────────
const pendingDelete = ref<DataspaceResponse | null>(null);
const deleting = ref(false);

async function onConfirmDelete() {
	const target = pendingDelete.value;
	if (!target) return;
	deleting.value = true;
	try {
		await deleteDataspace(target.id);
		dataspaces.value = dataspaces.value.filter((d) => d.id !== target.id);
		toasts.success(t("operator.dataspace.deleted", { name: target.label }));
		pendingDelete.value = null;
	} catch (e) {
		toasts.error(errorMessage(e));
	} finally {
		deleting.value = false;
	}
}

/** Only the newest read lands: a slow answer to an older one never replaces a newer list. */
let generation = 0;

async function loadList(first: boolean) {
	const mine = ++generation;
	try {
		const [ds, projects] = await Promise.all([listDataspaces(), listProjects()]);
		if (mine !== generation) return;
		dataspaces.value = ds;
		aliases.rememberDataspaces(ds);
		hasDesignerAccess.value = projects.some((p) =>
			p.effective_entitlements.some((e) => e.startsWith("designer:")),
		);
		error.value = "";
	} catch (e) {
		// A refresh that fails keeps the list it has: the list is not wrong, only not newer.
		if (mine === generation && first) error.value = errorMessage(e);
	} finally {
		if (mine === generation) loading.value = false;
	}
}

onMounted(async () => {
	// The route is public, so the router does not wait for the session: on a fresh load the
	// token is still being minted when this runs. Wait for it instead of reading it once.
	if (!isAuthenticated.value && !(await ensureSession())) {
		loading.value = false;
		return;
	}
	await loadList(true);
});

// The user's room: a space that came or went, a role that changed in a department.
useDataspaceListSync(() => {
	if (isAuthenticated.value) void loadList(false);
});
</script>

<template>
	<!-- Signed in, on a server without a valid license: the Operator is closed. -->
	<NoLicenseState v-if="isAuthenticated && licenseGate.required.value" />
	<!-- Signed in: the dataspace list -->
	<div
		v-else-if="isAuthenticated"
		data-habitus
		class="landing-page flex flex-col gap-6 bg-bg px-8 py-6 max-narrow:px-4"
	>
		<PageHeader>
			<template #title>{{ $t("nav.dataspaces") }}</template>

			<template #search>
				<HabInput
					v-model="search"
					v-page-search
					:icon="mdiMagnify"
					:placeholder="$t('operator.dataspace.search')"
					:aria-label="$t('operator.dataspace.search')"
					type="search"
					class="min-w-0 flex-1"
				/>
				<!-- The search is a remembered one — said, so a short list never reads as missing data. -->
				<div
					v-if="!searchIsDefault"
					class="flex items-center gap-1 whitespace-nowrap text-xxs text-fg-3"
				>
					<HabIcon
						:icon="mdiFilterCheckOutline"
						size="sm"
					/>
					{{ $t("common.viewState.restored") }}
					<HabButton
						variant="text"
						size="sm"
						@click="resetSearch"
					>
						{{ $t("common.actions.clear") }}
					</HabButton>
				</div>
				<span
					v-else-if="(search || narrowed) && !loading"
					class="whitespace-nowrap text-sm text-fg-3"
				>
					{{
						$t("operator.dataspace.resultCount", {
							n: filteredDataspaces.length,
							total: dataspaces.length,
						})
					}}
				</span>
				<HabSelect
					density="compact"
					label-position="inline"
					v-if="anyDepartment"
					v-model="locationFilter"
					:label="$t('spaces.filter.label')"
					:options="locationOptions"
				/>
			</template>

			<template #actions>
				<HabButton
					v-if="hasDesignerAccess"
					as="a"
					href="/designer"
					variant="outlined"
					tone="neutral"
					:icon="mdiPencilRuler"
					@click="follow($event, '/designer')"
				>
					{{ $t("operator.dataspace.openDesigner") }}
				</HabButton>
				<HabButton
					:icon="mdiPlus"
					@click="showCreate = true"
				>
					{{ $t("operator.dataspace.create") }}
				</HabButton>
			</template>
		</PageHeader>

		<PendingInvitationsBanner />

		<HabBanner
			v-if="error"
			tone="error"
			:title="error"
			:dismiss-label="$t('common.actions.close')"
			@dismiss="error = ''"
		/>

		<HabPanel
			v-if="loading"
			padding="lg"
		>
			<HabSkeleton
				:count="4"
				:height="16"
				class="flex flex-col gap-5"
			/>
		</HabPanel>

		<HabPanel
			v-else-if="!dataspaces.length && !error"
			padding="lg"
		>
			<HabEmptyState
				:icon="mdiDatabasePlusOutline"
				:title="$t('operator.dataspace.empty.title')"
				:note="$t('operator.dataspace.empty.hint')"
			>
				<template #action>
					<HabButton
						:icon="mdiPlus"
						@click="showCreate = true"
					>
						{{ $t("operator.dataspace.create") }}
					</HabButton>
				</template>
			</HabEmptyState>
		</HabPanel>

		<HabPanel
			v-else-if="!filteredDataspaces.length && dataspaces.length"
			padding="lg"
		>
			<HabEmptyState
				:icon="mdiMagnifyClose"
				:title="$t('operator.dataspace.noResults.title')"
				:note="$t('operator.dataspace.noResults.hint')"
			>
				<template #action>
					<HabButton
						variant="outlined"
						tone="neutral"
						@click="clearFilters"
					>
						{{ $t("common.actions.clear") }}
					</HabButton>
				</template>
			</HabEmptyState>
		</HabPanel>

		<HabPanel
			v-else-if="dataspaces.length"
			flush
		>
			<HabDataTable
				:label="$t('nav.dataspaces')"
				:columns="columns"
				:groups="groups"
				density="comfortable"
				@row-click="({ row }) => openDataspace(String(row.id))"
			>
				<template #[`cell:dataspace`]="{ row }">
					<!--
						A dataspace the license has closed to the Operator says so beside its name
						(`SpaceRestrictionBadge`), with the reason on hover: nobody opens it to learn it.
					-->
					<HabDataCell :note="dataspaceOf(row.id).description ?? undefined">
						<span class="flex min-w-0 items-center gap-2">
							<span class="truncate">{{ dataspaceOf(row.id).label }}</span>
							<SpaceRestrictionBadge
								kind="dataspace"
								:restriction="dataspaceOf(row.id).restriction"
								class="shrink-0"
							/>
						</span>
						<template #leading>
							<HabIconTile
								:icon="mdiDatabaseOutline"
								size="sm"
							/>
						</template>
					</HabDataCell>
				</template>

				<template #[`cell:department`]="{ row }">
					<SpaceLocationBadge :department="dataspaceOf(row.id).department" />
				</template>

				<template #[`cell:version`]="{ row }">
					<HabBadge>{{ dataspaceOf(row.id).project_version }}</HabBadge>
				</template>

				<template #[`cell:role`]="{ row }">
					<HabBadge>{{ roleLabel(dataspaceOf(row.id).role) }}</HabBadge>
				</template>

				<!--
					Stopped at the cell, or opening the menu — and the Enter that picks from it —
					would reach the row and open the dataspace instead.
				-->
				<template #[`cell:actions`]="{ row }">
					<span
						v-if="rowMenu(dataspaceOf(row.id)).length"
						class="inline-flex"
						@click.stop
						@keydown.stop
					>
						<HabMenu :items="rowMenu(dataspaceOf(row.id))">
							<template #trigger>
								<HabRowAction
									:label="
										$t('common.actions.moreFor', {
											name: dataspaceOf(row.id).label,
										})
									"
								/>
							</template>
						</HabMenu>
					</span>
				</template>
			</HabDataTable>
		</HabPanel>

		<CreateDataspaceDialog
			:show="showCreate"
			@close="showCreate = false"
			@created="onDataspaceCreated"
		/>

		<HabConfirm
			:open="!!pendingDelete"
			:cancel-label="$t('common.actions.cancel')"
			:close-label="$t('common.actions.close')"
			:title="$t('operator.dataspace.deleteConfirm', { name: pendingDelete?.label ?? '' })"
			:note="$t('operator.dataspace.deleteNote')"
			:confirm-label="$t('common.actions.delete')"
			:busy="deleting"
			@update:open="(open) => !open && (pendingDelete = null)"
			@confirm="onConfirmDelete"
		/>

		<!-- This view's own host: see `useOperatorToasts` for why the layout's is not here. -->
		<HabToastHost
			data-habitus
			:toasts="toasts.toasts.value"
			:duration="8000"
			:label="$t('common.toasts.label')"
			:dismiss-label="$t('common.actions.close')"
			@dismiss="toasts.dismiss"
		/>
	</div>

	<!-- Signed out: the landing hero -->
	<div
		v-else
		data-habitus
		class="landing-page hero flex items-center justify-center overflow-hidden bg-bg px-10 py-12 max-narrow:px-6 max-narrow:py-10"
	>
		<div
			class="flex w-full max-w-[1000px] items-center gap-20 max-narrow:flex-col-reverse max-narrow:gap-9 max-narrow:text-center"
		>
			<div class="min-w-0 flex-1">
				<!-- Off the type ladder on purpose: a landing wordmark is display type, and no token is. -->
				<h1
					class="mb-5 text-[clamp(3.2rem,6.5vw,5.5rem)] leading-none font-bold tracking-[-0.02em] text-accent-on"
				>
					{{ $t("common.appName") }}
				</h1>
				<p
					class="mb-4 max-w-[460px] text-[clamp(1.0625rem,1.6vw,1.25rem)] leading-[1.65] text-fg-2 max-narrow:max-w-none"
				>
					{{ $t("common.tagline") }}
				</p>
				<!-- What Notitia is, for whoever lands here before signing in: quieter than the tagline above it. -->
				<p
					class="mb-10 max-w-[460px] text-title leading-[1.65] text-fg-3 max-narrow:max-w-none"
				>
					{{ $t("common.lead") }}
				</p>
				<div class="flex flex-wrap gap-3 max-narrow:justify-center">
					<HabButton
						size="lg"
						@click="login()"
					>
						{{ $t("nav.signIn") }}
					</HabButton>
				</div>
			</div>

			<!-- The mark writes itself in: the one place the draw-in is the point. -->
			<BrandMark
				:size="260"
				color="var(--hab-accent)"
				class="flex-none size-[clamp(160px,22vw,260px)] max-narrow:size-[clamp(120px,40vw,180px)]"
			/>
		</div>
	</div>
</template>

<style scoped>
/*
  Both states fill the viewport below the landing bar, so the page ground reaches the
  bottom edge however short the list is. The bar's height is the layout's to say.
*/
.landing-page {
	min-height: calc(100dvh - var(--landing-bar-height, 3.5rem));
}

/*
  The hero's ground: a faint dot grid and two washes of the section's accent, mixed
  with `color-mix` so they follow the accent and the theme rather than the four
  literal teals, limes and greens they used to be.
*/
.hero {
	background-image:
		radial-gradient(circle, var(--hab-line) 1px, transparent 1px),
		radial-gradient(
			ellipse 80% 70% at 8% 12%,
			color-mix(in srgb, var(--hab-accent) 18%, transparent) 0%,
			transparent 58%
		),
		radial-gradient(
			ellipse 60% 55% at 92% 88%,
			color-mix(in srgb, var(--hab-accent) 10%, transparent) 0%,
			transparent 58%
		);
	background-size:
		28px 28px,
		auto,
		auto;
}
</style>
